// Rhythm Echo exercise state machine (issue #3473): a pure reducer over the phases of a session,
// driven by clock ticks and taps. No React, no DOM, no timers, no audio. The page holds the state
// in useReducer, polls the audio clock on requestAnimationFrame and dispatches `tick`, dispatches
// `tap` with each tap's aligned audio-clock time, renders from the selectors below and schedules
// sounds from the timelines the state exposes. Stories drive it the same way with fake ticks.
//
// Every time in this module is absolute ms on the audio clock (whatever clock `startMs` was read
// from). Taps arrive already aligned (LearnAudioSession.alignedTapMs); scoring shifts them relative
// to the tap bar start, which is what scorePattern expects.
//
// Phases, per session:
//
//   idle ──start(startMs)──▶ listen ──tick at tapBarStartMs (Go!)──▶ tap
//                              │                                       │
//                              │ stop                                  │ tick at tapsEndMs, or stop
//                              ▼                                       ▼
//                             result (taps so far)                    result (taps so far)
//
//   result ──replay(startMs)──▶ replay ──tick at replay end──▶ result
//   result ──next(startMs)──▶ the next pattern's listen phase, or summary after the last
//   result ──retry(startMs)──▶ the same pattern again, once per pattern
//   summary ──restart(patterns, startMs)──▶ a fresh session
//
// The pattern phases (listen, tap) are a function of the clock and the pattern's RhythmTimeline: a
// tick moves the phase forward to wherever the time has reached, never backwards, and one tick may
// cross several phases when the clock jumps. There is no count-in (issue #3519) and no silent cue
// beats (issue #3588): `listen` covers one silent lead beat and then the play bar, over whose third
// and fourth beats Ready and Set pulse, and `tap` begins on Go!, the tap bar's first beat, straight
// after the play bar. Taps are accepted from tapsOpenMs (Set, a full beat before Go!, to catch a
// keen singer) to tapsEndMs; earlier or later taps are dropped without penalty. So the tap gate
// (isTapOpen) opens a beat before the tap button looks open (isTapButtonOpen, from Go!). The tap
// phase ends on its own at tapsEndMs and scores itself; `stop` scores immediately with the taps so
// far, so the remaining beats come out missed and the session keeps moving.
// Reaching the result appends it to `results`, so in the result and replay phases
// `results[patternIndex]` is the current pattern's result.
//
// Try again: from a result (or mid-replay) a singer may retry the current pattern once. The
// retry drops the pattern's result and runs the same pattern again from its listen phase; its result
// then replaces the first attempt everywhere (score, best streak, summary squares). The pattern
// is marked in `retriedPatterns` so a second retry is a no-op and canRetry is false.
//
// Sing Rhythm (issue #3723) is this machine with `input: 'voice'`: a "tap" is then the start of a
// sung note, as the microphone timed it, aligned through the voice offset. Two things differ, both
// because a sung note reaches the page late - by the microphone's own delay and the detector's:
//
//   - Notes are accepted from one band before Go!, not from Set. The pattern's own last sounds come
//     out of the speaker during the play bar's fourth beat and into the microphone, and from Set
//     they would be taken for the singer starting early.
//   - The tap phase ends `settleMs` after tapsEndMs rather than at it, so a note sung on the last
//     beat has arrived before the pattern is scored. A note is still judged on its own aligned
//     time: one after tapsEndMs is dropped, as a late tap is.

import { rhythmLevelParams } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import {
  lastReachedIndex,
  patternOnsetsMs,
  PatternResult,
  patternTimeline,
  rhythmBeatsPerBar,
  RhythmInput,
  RhythmPattern,
  RhythmSessionResult,
  RhythmTimeline,
  scorePattern,
  summarizeSession,
} from '../../../../../common/src/rhythm_echo.ts';
import { VisibleCue, visibleCue } from '../learn_cue.ts';

// ---- State -----------------------------------------------------------------------------------

export type RhythmPhase = 'idle' | 'listen' | 'tap' | 'result' | 'replay' | 'summary';

// The phases a pattern passes through before its result, in order
export type RhythmPatternPhase = 'listen' | 'tap';

const patternPhases: RhythmPatternPhase[] = ['listen', 'tap'];

// "Hear both again": the pattern's onsets for one bar from `startMs`, then the recorded taps one
// bar later, both as absolute times the page schedules percussion for
export interface RhythmReplay {
  startMs: number;
  patternOnsetsMs: number[];
  tapOnsetsMs: number[]; // the taps that counted, in the second bar
  endMs: number; // two bars plus the band, the same slack the tap phase allows
}

interface SessionFields {
  level: LearnLevel;
  patterns: RhythmPattern[];
  patternIndex: number;
  results: PatternResult[]; // one per finished pattern, in order
  retriedPatterns: number[]; // indices of the patterns already retried this session
  nowMs: number; // the last clock reading seen by start, tick, next, replay, retry or restart
  input?: RhythmInput; // how the rhythm is given back; absent is 'tap'
  settleMs?: number; // how long after tapsEndMs the tap phase waits for input still on its way; absent is 0
}

// What a session is given back with (see the header's note on Sing Rhythm)
export interface RhythmSessionOptions {
  input?: RhythmInput;
  settleMs?: number;
}

export interface RhythmIdleState extends SessionFields {
  phase: 'idle';
}

export interface RhythmPatternState extends SessionFields {
  phase: RhythmPatternPhase;
  timeline: RhythmTimeline;
  tapsMs: number[]; // accepted taps so far, absolute
}

export interface RhythmResultState extends SessionFields {
  phase: 'result';
  timeline: RhythmTimeline;
  result: PatternResult; // the same object as results[patternIndex]
}

export interface RhythmReplayState extends SessionFields {
  phase: 'replay';
  timeline: RhythmTimeline;
  result: PatternResult;
  replay: RhythmReplay;
}

export interface RhythmSummaryState extends SessionFields {
  phase: 'summary';
  summary: RhythmSessionResult;
}

export type RhythmEchoState =
  RhythmIdleState | RhythmPatternState | RhythmResultState | RhythmReplayState | RhythmSummaryState;

// ---- Actions ---------------------------------------------------------------------------------

export type RhythmEchoAction =
  | { type: 'start'; startMs: number } // idle: begin the first pattern at startMs (the button tap)
  | { type: 'tick'; nowMs: number } // the audio clock now; advances pattern phases and ends replays
  | { type: 'tap'; tapMs: number } // an aligned tap; kept when inside the tapping window
  | { type: 'stop' } // during a pattern: score now with the taps so far
  | { type: 'next'; startMs: number } // result or replay: the next pattern at startMs, or the summary
  | { type: 'replay'; startMs: number } // result or replay: hear both again from startMs
  | { type: 'retry'; startMs: number } // result or replay, once per pattern: the same pattern again at startMs
  | { type: 'restart'; patterns: RhythmPattern[]; startMs: number }; // a fresh session, from anywhere

// The state a page starts from; `patterns` come from generateSession (or a fixture)
export function initialRhythmEchoState(
  level: LearnLevel,
  patterns: RhythmPattern[],
  options: RhythmSessionOptions = {},
): RhythmIdleState {
  return {
    phase: 'idle',
    level,
    patterns,
    patternIndex: 0,
    results: [],
    retriedPatterns: [],
    nowMs: 0,
    ...sessionOptions(options),
  };
}

// Only the options that are set, so a tapped session's state carries no keys of the sung one
function sessionOptions({ input, settleMs }: RhythmSessionOptions): RhythmSessionOptions {
  return { ...(input !== undefined && { input }), ...(settleMs !== undefined && { settleMs }) };
}

export function reduceRhythmEcho(state: RhythmEchoState, action: RhythmEchoAction): RhythmEchoState {
  switch (action.type) {
    case 'start':
      return state.phase === 'idle' ? startPattern(state, 0, action.startMs) : state;
    case 'tick':
      return tick(state, action.nowMs);
    case 'tap':
      return tap(state, action.tapMs);
    case 'stop':
      return isPatternState(state) ? finishPattern(state) : state;
    case 'next':
      return next(state, action.startMs);
    case 'replay':
      return replay(state, action.startMs);
    case 'retry':
      return retry(state, action.startMs);
    case 'restart':
      return startPattern(initialRhythmEchoState(state.level, action.patterns, state), 0, action.startMs);
  }
}

// ---- Transitions -----------------------------------------------------------------------------

function isPatternState(state: RhythmEchoState): state is RhythmPatternState {
  return patternPhases.includes(state.phase as RhythmPatternPhase);
}

function sessionFields(state: RhythmEchoState): SessionFields {
  const { level, patterns, patternIndex, results, retriedPatterns, nowMs } = state;
  return { level, patterns, patternIndex, results, retriedPatterns, nowMs, ...sessionOptions(state) };
}

function startPattern(state: RhythmEchoState, patternIndex: number, startMs: number): RhythmPatternState {
  return {
    ...sessionFields(state),
    phase: 'listen',
    patternIndex,
    nowMs: startMs,
    timeline: patternTimeline(state.patterns[patternIndex], state.level, startMs),
    tapsMs: [],
  };
}

// The phase the timeline is in at `nowMs`, or 'result' once tapping has closed
function phaseAtMs(timeline: RhythmTimeline, nowMs: number, settleMs: number): RhythmPatternPhase | 'result' {
  if (nowMs >= timeline.tapsEndMs + settleMs) {
    return 'result';
  }
  if (nowMs >= timeline.tapBarStartMs) {
    return 'tap';
  }
  return 'listen';
}

function tick(state: RhythmEchoState, nowMs: number): RhythmEchoState {
  if (isPatternState(state)) {
    const reached = phaseAtMs(state.timeline, nowMs, state.settleMs ?? 0);
    if (reached === 'result') {
      return finishPattern({ ...state, nowMs });
    }
    // never backwards: a tick that reads earlier than the phase already reached keeps the phase
    const phase = patternPhases.indexOf(reached) > patternPhases.indexOf(state.phase) ? reached : state.phase;
    return { ...state, phase, nowMs };
  }
  if (state.phase === 'replay') {
    if (nowMs >= state.replay.endMs) {
      const { replay: _replay, ...rest } = state;
      return { ...rest, phase: 'result', nowMs };
    }
    return { ...state, nowMs };
  }
  return { ...state, nowMs };
}

function tap(state: RhythmEchoState, tapMs: number): RhythmEchoState {
  if (!isPatternState(state)) {
    return state;
  }
  if (tapMs < inputOpenMs(state) || tapMs >= state.timeline.tapsEndMs) {
    return state; // a tap before tapping opened, or after it closed: ignored, never penalised
  }
  return { ...state, tapsMs: [...state.tapsMs, tapMs] };
}

// When input starts to count: taps from Set, sung notes from one band before Go! (see the header)
function inputOpenMs(state: RhythmPatternState): number {
  const { tapsOpenMs, tapBarStartMs, bandMs } = state.timeline;
  return state.input === 'voice' ? tapBarStartMs - bandMs : tapsOpenMs;
}

// Scores the current pattern with the taps so far and moves to its result
function finishPattern(state: RhythmPatternState): RhythmResultState {
  const { timeline, tapsMs, ...rest } = state;
  const pattern = state.patterns[state.patternIndex];
  const result = scorePattern(
    pattern,
    state.level,
    tapsMs.map((tapMs) => tapMs - timeline.tapBarStartMs),
    state.input,
  );
  return { ...rest, phase: 'result', timeline, result, results: [...state.results, result] };
}

function next(state: RhythmEchoState, startMs: number): RhythmEchoState {
  if (state.phase !== 'result' && state.phase !== 'replay') {
    return state;
  }
  const nextIndex = state.patternIndex + 1;
  if (nextIndex < state.patterns.length) {
    return startPattern(state, nextIndex, startMs);
  }
  return { ...sessionFields(state), phase: 'summary', nowMs: startMs, summary: summarizeSession(state.results) };
}

function replay(state: RhythmEchoState, startMs: number): RhythmEchoState {
  if (state.phase !== 'result' && state.phase !== 'replay') {
    return state;
  }
  const { level, timeline, result } = state;
  const barMs = rhythmBeatsPerBar * timeline.beatMs;
  const onsetsMs = patternOnsetsMs(result.pattern, rhythmLevelParams[level].bpm);
  return {
    ...sessionFields(state),
    phase: 'replay',
    nowMs: startMs,
    timeline,
    result,
    replay: {
      startMs,
      patternOnsetsMs: onsetsMs.map((onsetMs) => startMs + onsetMs),
      tapOnsetsMs: result.tapsMs.map((tapMs) => startMs + barMs + tapMs),
      endMs: startMs + 2 * barMs + timeline.bandMs,
    },
  };
}

// The same pattern again from its lead beat, with the first attempt's result dropped so the
// retry's result takes its place in `results`
function retry(state: RhythmEchoState, startMs: number): RhythmEchoState {
  if (!canRetry(state)) {
    return state;
  }
  const { patternIndex } = state;
  return startPattern(
    {
      ...state,
      results: state.results.slice(0, patternIndex), // results.length === patternIndex + 1 here
      retriedPatterns: [...state.retriedPatterns, patternIndex],
    },
    patternIndex,
    startMs,
  );
}

// ---- Selectors -------------------------------------------------------------------------------

// True from the first pattern until the summary: leaving now would discard the session, and only
// a session that reaches the summary is recorded
export function isInProgress(state: RhythmEchoState): boolean {
  return state.phase !== 'idle' && state.phase !== 'summary';
}

export function currentPattern(state: RhythmEchoState): RhythmPattern | undefined {
  return state.patterns[state.patternIndex];
}

// The most recent play onset that has sounded while the pattern plays, for lightsForPlayback;
// undefined before the first onset and outside the listen phase
export function playbackOnsetIndex(state: RhythmEchoState): number | undefined {
  if (state.phase !== 'listen') {
    return undefined;
  }
  return lastReachedIndex(state.timeline.playOnsetsMs, state.nowMs);
}

// The Ready · Set · Go! word pulsing now, if any, for the cue row. Evaluated through both pattern
// phases: Ready and Set pulse over the play bar's third and fourth beats, in the listen phase, and
// Go! on the tap bar's first beat, in the tap phase, after which nothing follows.
export function cueNow(state: RhythmEchoState): VisibleCue | undefined {
  if (!isPatternState(state)) {
    return undefined;
  }
  return visibleCue(state.nowMs, state.timeline.cueMs, state.timeline.beatMs);
}

// How far through the tap bar the clock is, 0..1, during the tap phase; else undefined
export function tapBarProgress(state: RhythmEchoState): number | undefined {
  if (state.phase !== 'tap') {
    return undefined;
  }
  const { tapBarStartMs, beatMs } = state.timeline;
  const fraction = (state.nowMs - tapBarStartMs) / (rhythmBeatsPerBar * beatMs);
  return Math.min(Math.max(fraction, 0), 1);
}

// Taps accepted so far this pattern, absolute; empty outside the pattern phases
export function tapsAccepted(state: RhythmEchoState): number[] {
  return isPatternState(state) ? state.tapsMs : [];
}

// Whether a tap dispatched now would be kept: inside the tapping window of a pattern phase, which
// opens on Set, a beat before the tap button looks open (see isTapButtonOpen). The gate for the
// page's tap handler.
export function isTapOpen(state: RhythmEchoState, nowMs = state.nowMs): boolean {
  return isPatternState(state) && nowMs >= inputOpenMs(state) && nowMs < state.timeline.tapsEndMs;
}

// Whether the tap button looks open (its `open` prop, caption "now"): from Go!, i.e. the tap phase.
// Only the look: a keen tap from Set is already kept (isTapOpen) while the button still reads
// "on Go!".
export function isTapButtonOpen(state: RhythmEchoState): boolean {
  return state.phase === 'tap' && state.nowMs >= state.timeline.tapBarStartMs;
}

// Whether "Try again" is offered: at a result or mid-replay, and this pattern not yet retried
export function canRetry(state: RhythmEchoState): boolean {
  return (state.phase === 'result' || state.phase === 'replay') && !state.retriedPatterns.includes(state.patternIndex);
}

// The current pattern's result, in the result and replay phases
export function currentResult(state: RhythmEchoState): PatternResult | undefined {
  return state.phase === 'result' || state.phase === 'replay' ? state.result : undefined;
}

// During a replay, which onset in each row has sounded most recently: `pattern` indexes the
// pattern's onsets (lightsForPlayback), `tap` indexes result.tapsMs. Undefined before each row's
// first sound and outside the replay phase.
export function replayOnsetIndices(state: RhythmEchoState): { pattern?: number; tap?: number } {
  if (state.phase !== 'replay') {
    return {};
  }
  return {
    pattern: lastReachedIndex(state.replay.patternOnsetsMs, state.nowMs),
    tap: lastReachedIndex(state.replay.tapOnsetsMs, state.nowMs),
  };
}

// During a replay, the beat (index into result.beats) that owns the most recent tap that has
// sounded, so the "what you tapped" row can light beat by beat; undefined before the first tap
export function replayTapBeatIndex(state: RhythmEchoState): number | undefined {
  const { tap } = replayOnsetIndices(state);
  if (state.phase !== 'replay' || tap === undefined) {
    return undefined;
  }
  const tapMs = state.result.tapsMs[tap];
  const beat = state.result.beats.findIndex(({ tapsMs }) => tapsMs.includes(tapMs));
  return beat === -1 ? undefined : beat;
}
